import type { EChartsOption } from "echarts";
import type { EChartsThemeTokens } from "@/shared/charts/theme/create-echarts-theme";
import type { AxisRange } from "@/shared/charts/utils/compute-nice-axis-range";
import type {
  TimeComparisonLineChartConfig,
  TimeComparisonLineChartPoint,
} from "@/shared/charts/components/time-comparison-line-chart/types";
import { formatTimeAxisLabel } from "@/shared/charts/utils/format-time-axis-label";

interface TimeComparisonLineChartColors {
  current: string;
  comparison: string;
  now: string;
  nowText: string;
}

function formatValue(value: number, config: TimeComparisonLineChartConfig): string {
  return config.valueFormatter?.(value) ?? value.toLocaleString();
}

function createValueFormatter(config: TimeComparisonLineChartConfig) {
  return (value: unknown) => {
    const scalarValue = Array.isArray(value) ? value.at(-1) : value;
    return typeof scalarValue === "number"
      ? formatValue(scalarValue, config)
      : String(scalarValue ?? "");
  };
}

function createSeries(
  currentData: TimeComparisonLineChartPoint[],
  comparisonData: TimeComparisonLineChartPoint[],
  now: number | null,
  config: TimeComparisonLineChartConfig,
  tokens: EChartsThemeTokens,
  colors: TimeComparisonLineChartColors
): NonNullable<EChartsOption["series"]> {
  const valueFormatter = createValueFormatter(config);

  const comparisonSeries = {
    id: "time-comparison-line-chart-comparison",
    name: config.comparisonSeriesLabel,
    type: "line" as const,
    dimensions: ["time", config.comparisonSeriesLabel],
    encode: {
      x: "time",
      y: config.comparisonSeriesLabel,
      tooltip: config.comparisonSeriesLabel,
    },
    data: comparisonData.map((point) => [point.timestamp, point.value]),
    tooltip: { valueFormatter },
    showSymbol: true,
    symbol: "circle",
    symbolSize: 4,
    lineStyle: { color: colors.comparison, width: 1.5 },
    itemStyle: {
      color: tokens.backgroundColor,
      borderColor: colors.comparison,
      borderWidth: 1,
    },
  };
  const currentSeries = {
    id: "time-comparison-line-chart-current",
    name: config.currentSeriesLabel,
    type: "line" as const,
    dimensions: ["time", config.currentSeriesLabel],
    encode: { x: "time", y: config.currentSeriesLabel, tooltip: config.currentSeriesLabel },
    data: currentData.map((point) => [point.timestamp, point.value]),
    tooltip: { valueFormatter },
    showSymbol: true,
    symbol: "circle",
    symbolSize: 6,
    lineStyle: { color: colors.current, width: 2 },
    itemStyle: {
      color: tokens.backgroundColor,
      borderColor: colors.current,
      borderWidth: 2,
    },
    markLine: {
      silent: true,
      symbol: ["none", "none"],
      data: [
        ...(config.thresholds ?? []).map((threshold) => ({
          name: threshold.label,
          yAxis: threshold.value,
          lineStyle: { color: tokens.axisLineColor, width: 1, type: "solid" as const },
          label: {
            show: true,
            formatter: threshold.label,
            position: "insideEndTop" as const,
            color: tokens.mutedTextColor,
            backgroundColor: tokens.backgroundColor,
            padding: [4, 6] as [number, number],
            fontFamily: tokens.fontFamily,
            fontSize: 13,
            fontWeight: 700,
          },
        })),
        ...(now === null
          ? []
          : [
              {
                name: config.nowLabel,
                xAxis: now,
                lineStyle: { color: colors.now, width: 2, type: "dashed" as const },
                label: {
                  show: true,
                  formatter: config.nowLabel,
                  position: "insideStartTop" as const,
                  color: colors.nowText,
                  backgroundColor: colors.now,
                  borderRadius: 4,
                  padding: [5, 8] as [number, number],
                  fontFamily: tokens.fontFamily,
                  fontSize: 13,
                  fontWeight: 700,
                },
              },
            ]),
      ],
    },
  };

  return comparisonData.length > 0 ? [comparisonSeries, currentSeries] : [currentSeries];
}

/**
 * Builds the chart's static "shell": legend, tooltip, grid, and axis styling. This never depends
 * on live data/time, so it should be passed to ECharts once (declaratively) and left alone
 * afterwards. Rebuilding these components on every viewport tick would rebuild the legend's
 * clickable graphics from scratch each time, which can race with a user's click and make it look
 * like the toggle silently didn't register.
 */
export function createTimeComparisonLineChartShellOption(
  config: TimeComparisonLineChartConfig,
  tokens: EChartsThemeTokens,
  colors: TimeComparisonLineChartColors,
  hasComparisonData = true
): EChartsOption {
  return {
    animation: false,
    aria: {
      enabled: true,
      label: { enabled: true, description: config.ariaDescription },
    },
    backgroundColor: tokens.backgroundColor,
    color: [colors.current, colors.comparison],
    textStyle: { color: tokens.textColor, fontFamily: tokens.fontFamily },
    grid: { top: 64, right: 28, bottom: 32, left: 18, containLabel: true },
    legend: {
      top: 8,
      left: "center",
      itemGap: 24,
      itemWidth: 36,
      itemHeight: 12,
      textStyle: { color: tokens.mutedTextColor, fontFamily: tokens.fontFamily, fontSize: 14 },
      data: hasComparisonData
        ? [config.currentSeriesLabel, config.comparisonSeriesLabel]
        : [config.currentSeriesLabel],
    },
    tooltip: {
      trigger: "axis",
      renderMode: "richText",
      confine: true,
      backgroundColor: tokens.tooltipBackgroundColor,
      borderColor: tokens.tooltipBorderColor,
      borderWidth: 1,
      padding: [8, 10],
      textStyle: {
        color: tokens.textColor,
        fontFamily: tokens.fontFamily,
        fontSize: 12,
        lineHeight: 19,
      },
      axisPointer: {
        type: "line",
        snap: true,
        lineStyle: { color: tokens.axisLineColor, width: 1 },
      },
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: tokens.axisLineColor } },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        hideOverlap: true,
        color: tokens.mutedTextColor,
        fontFamily: tokens.fontFamily,
        fontSize: 16,
        fontWeight: 700,
        margin: 12,
      },
    },
    yAxis: {
      type: "value",
      axisLabel: {
        color: tokens.mutedTextColor,
        fontFamily: tokens.fontFamily,
        fontSize: 16,
        fontWeight: 700,
        formatter: createValueFormatter(config),
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: tokens.gridLineColor, width: 1 } },
    },
  };
}

/** Builds the viewport update (x-axis bounds) without touching series or other chart components. */
export function createTimeComparisonLineChartAxisOption(
  rangeStart: number,
  rangeEnd: number
): EChartsOption {
  return {
    xAxis: {
      min: rangeStart,
      max: rangeEnd,
      axisLabel: {
        formatter: (value: number) =>
          formatTimeAxisLabel(value, { min: rangeStart, max: rangeEnd }),
      },
    },
  };
}

/** Builds series, marker, and y-axis updates applied only when fetched data changes. */
export function createTimeComparisonLineChartDataOption(
  currentData: TimeComparisonLineChartPoint[],
  comparisonData: TimeComparisonLineChartPoint[],
  now: number | null,
  config: TimeComparisonLineChartConfig,
  tokens: EChartsThemeTokens,
  colors: TimeComparisonLineChartColors,
  yAxisRange: AxisRange
): EChartsOption {
  return {
    yAxis: {
      min: yAxisRange.min,
      max: yAxisRange.max,
      interval: yAxisRange.interval,
    },
    series: createSeries(currentData, comparisonData, now, config, tokens, colors),
  };
}
